import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, Button, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Divider, Tabs, Tab, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InsertChartIcon from '@mui/icons-material/InsertChart';

// Mock subscribers data
const subscribers = [
  { id: 1, email: 'emma@example.com', name: 'Emma Johnson', status: 'Active', joinDate: '2023-05-10', openRate: '78%' },
  { id: 2, email: 'michael@example.com', name: 'Michael Chen', status: 'Active', joinDate: '2023-05-05', openRate: '65%' },
  { id: 3, email: 'sophia@example.com', name: 'Sophia Williams', status: 'Inactive', joinDate: '2023-04-20', openRate: '32%' },
  { id: 4, email: 'james@example.com', name: 'James Brown', status: 'Active', joinDate: '2023-05-15', openRate: '91%' },
  { id: 5, email: 'maria@example.com', name: 'Maria Garcia', status: 'Active', joinDate: '2023-05-02', openRate: '84%' },
  { id: 6, email: 'david@example.com', name: 'David Lee', status: 'Unsubscribed', joinDate: '2023-03-15', openRate: '45%' },
  { id: 7, email: 'olivia@example.com', name: 'Olivia Taylor', status: 'Active', joinDate: '2023-05-12', openRate: '72%' },
  { id: 8, email: 'noah@example.com', name: 'Noah Wilson', status: 'Active', joinDate: '2023-04-28', openRate: '68%' },
];

// Mock campaigns data
const campaigns = [
  { 
    id: 1, 
    title: 'May Market Update', 
    sentDate: '2023-05-10', 
    recipients: 2450, 
    openRate: '68%', 
    clickRate: '23%',
    status: 'Sent' 
  },
  { 
    id: 2, 
    title: 'New Feature Announcement', 
    sentDate: '2023-04-28', 
    recipients: 2380, 
    openRate: '72%', 
    clickRate: '31%',
    status: 'Sent' 
  },
  { 
    id: 3, 
    title: 'Trading Strategy Webinar', 
    sentDate: '2023-05-20', 
    recipients: 2500, 
    openRate: null, 
    clickRate: null,
    status: 'Scheduled' 
  },
  { 
    id: 4, 
    title: 'Summer Promotion', 
    sentDate: null, 
    recipients: null, 
    openRate: null, 
    clickRate: null,
    status: 'Draft' 
  },
];

function Newsletter() {
  const [tabValue, setTabValue] = useState(0);
  const [openCampaignDialog, setOpenCampaignDialog] = useState(false);
  const [openSubscriberDialog, setOpenSubscriberDialog] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Newsletter Management</Typography>
      </Box>

      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        variant="fullWidth"
        sx={{ mb: 3 }}
      >
        <Tab icon={<InsertChartIcon />} label="Overview" />
        <Tab icon={<SendIcon />} label="Campaigns" />
        <Tab icon={<ListAltIcon />} label="Subscribers" />
      </Tabs>

      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Subscribers
                </Typography>
                <Typography variant="h3">2,572</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  +5.2% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Open Rate
                </Typography>
                <Typography variant="h3">68.7%</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  +2.1% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Click Rate
                </Typography>
                <Typography variant="h3">24.3%</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  +3.7% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Unsubscribe Rate
                </Typography>
                <Typography variant="h3">0.8%</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  -0.2% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Recent Performance</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is a placeholder for a performance chart showing open rates, click rates, and growth over time.
              </Typography>
              <Box sx={{ 
                height: 300, 
                width: '100%', 
                bgcolor: 'action.hover', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Typography variant="body2">Performance Chart Placeholder</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Divider sx={{ mb: 2 }} />
              <Button 
                variant="contained" 
                fullWidth 
                startIcon={<SendIcon />}
                onClick={() => setOpenCampaignDialog(true)}
                sx={{ mb: 2 }}
              >
                Create Campaign
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenSubscriberDialog(true)}
                sx={{ mb: 2 }}
              >
                Add Subscriber
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                startIcon={<ListAltIcon />}
              >
                Import Subscribers
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Campaigns Tab */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<SendIcon />}
              onClick={() => setOpenCampaignDialog(true)}
            >
              Create Campaign
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent Date</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Open Rate</TableCell>
                  <TableCell>Click Rate</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>{campaign.title}</TableCell>
                    <TableCell>
                      <Chip 
                        label={campaign.status} 
                        color={
                          campaign.status === 'Sent' ? 'success' : 
                          campaign.status === 'Scheduled' ? 'primary' : 
                          'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{campaign.sentDate || 'Not sent'}</TableCell>
                    <TableCell>{campaign.recipients || '-'}</TableCell>
                    <TableCell>{campaign.openRate || '-'}</TableCell>
                    <TableCell>{campaign.clickRate || '-'}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" sx={{ mr: 1 }}>
                        {campaign.status === 'Draft' ? 'Edit' : 'View'}
                      </Button>
                      {campaign.status === 'Draft' && (
                        <Button size="small" variant="contained" color="primary">
                          Send
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Subscribers Tab */}
      {tabValue === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenSubscriberDialog(true)}
              sx={{ mr: 2 }}
            >
              Add Subscriber
            </Button>
            <Button variant="outlined">
              Import Subscribers
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Join Date</TableCell>
                  <TableCell>Open Rate</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscribers.map((subscriber) => (
                  <TableRow key={subscriber.id}>
                    <TableCell>{subscriber.email}</TableCell>
                    <TableCell>{subscriber.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={subscriber.status} 
                        color={
                          subscriber.status === 'Active' ? 'success' : 
                          subscriber.status === 'Inactive' ? 'warning' : 
                          'error'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{subscriber.joinDate}</TableCell>
                    <TableCell>{subscriber.openRate}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" sx={{ mr: 1 }}>
                        Edit
                      </Button>
                      <Button size="small" variant="outlined" color="error">
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={openCampaignDialog} onClose={() => setOpenCampaignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Campaign Title"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Subject Line"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="From Name"
              defaultValue="AI Trading Platform"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Reply-to Email"
              defaultValue="support@aitrading.com"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Campaign Content"
              multiline
              rows={10}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCampaignDialog(false)}>Cancel</Button>
          <Button 
            variant="outlined" 
            onClick={() => setOpenCampaignDialog(false)}
            sx={{ mr: 1 }}
          >
            Save as Draft
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setOpenCampaignDialog(false)}
            startIcon={<SendIcon />}
          >
            Send Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subscriber Dialog */}
      <Dialog open={openSubscriberDialog} onClose={() => setOpenSubscriberDialog(false)}>
        <DialogTitle>Add New Subscriber</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
            />
            <TextField
              margin="normal"
              fullWidth
              label="First Name"
            />
            <TextField
              margin="normal"
              fullWidth
              label="Last Name"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubscriberDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => setOpenSubscriberDialog(false)}
          >
            Add Subscriber
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Newsletter;
